'use client';
import { useTranslations } from 'next-intl';
import { type FC, useEffect, useMemo, useState } from 'react';
import { ProductLineElementLibrary } from '@/app/components/icons/ProductLineElementLibrary';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { useFlyTarget } from '@/app/components/shared/FlyOverlay/use-fly-target';
import { FormSubmitButton } from '@/app/components/shared/FormSubmitButton';
import { TemplateCard } from '@/app/components/shared/TemplateCard';
import { useToast } from '@/app/components/ui/use-toast';
import { RESOLUTION_MAP } from '@/app/constants';
import { useDevice, useFetchRecreateData } from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import type { TemplateFormParams } from '@/app/stores/slices/task-form';
import type {
  FormImagePrompt,
  ImageUploadFile,
  ImageUploadFileInfo,
  ServiceError,
  TemplateSettings,
} from '@/app/types';
import { reportEvent, validateUserPermissionTemplateSettings } from '@/app/utils';
import { useTemplatesPanel } from '../PanelContext';
import { ImageUploadSection } from './_components/ImageUploadSection';
import { SettingsSection } from './_components/SettingsSection';
import { TemplateDescription } from './_components/TemplateDescription';
import type { ImageSample, SupportedSettings, TemplateData, TemplateExampleImageResource } from './types';
import { useImageMerge } from './use-image-merge';
import { getSupportedSettingsFromTemplate, sanitizeTemplateSettings } from './utils';

const CROP_RATIO_PATTERN = /^[1-9]\d*:[1-9]\d*$/; // 裁剪比例格式验证正则（不允许0和前导零）

const DEFAULT_CREATION_RESOLUTION = Object.keys(RESOLUTION_MAP)[0] as keyof typeof RESOLUTION_MAP;

interface TemplateFormProps {
  templateDetail: {
    data?: TemplateData;
    error?: ServiceError;
  };
}

const TemplateForm: FC<TemplateFormProps> = ({ templateDetail }) => {
  const { toast } = useToast();
  const t = useTranslations();
  const store = useStoreApi();
  const { formParams, updateTemplateFormParams, setFormParams, userInfo } = useStore((state) => ({
    formParams: state.sceneFormParams,
    updateTemplateFormParams: state.updateTemplateFormParams,
    setFormParams: state.setFormParams,
    userInfo: state.userInfo,
  }));
  const { imagePrompts, settings } = formParams;
  const { supportsHover } = useDevice();

  // 模版信息（名字、描述、图例）
  const [templateDescription, setTemplateDescription] = useState<string>('');
  const [exampleImage, setExampleImage] = useState<TemplateExampleImageResource | null>(null);
  const resource = templateDetail.data?.creations?.find((creation) => creation.name === 'portrait')?.resource;

  const [imageUploadCount, setImageUploadCount] = useState<number>(0);
  const [imageUploadHints, setImageUploadHints] = useState<string[]>([]);
  const [uploadingImages, setUploadingImages] = useState<number[]>([]);
  const [processingImageIdx, setProcessingImageIdx] = useState<number | null>(null);
  const [imageSamples, setImageSamples] = useState<ImageSample[]>([]);
  // 简化：不使用 ref 回调触发

  const [forceCropRatio, setForceCropRatio] = useState<string>(''); // 强制裁剪比例
  const [activeCropRatios, setActiveCropRatios] = useState<string[]>([]); // 可选裁剪比例
  const [defaultCropIndex, setDefaultCropIndex] = useState<number>(0); // 默认裁剪比例索引
  const flyEndTargetRef = useFlyTarget();

  // 决定是否显示相应的配置项控件
  const [supportedSettings, setSupportedSettings] = useState<SupportedSettings>({
    sampleCount: false,
    aspectRatio: false,
    hasAudio: false,
  });

  useFetchRecreateData();

  const maxImageSize = useMemo(() => {
    return (
      RESOLUTION_MAP[formParams.creationResolution as keyof typeof RESOLUTION_MAP] ||
      RESOLUTION_MAP[DEFAULT_CREATION_RESOLUTION]
    );
  }, [formParams.creationResolution]);

  const { triggerMergeOnUpload } = useImageMerge(maxImageSize);

  const updateImagePrompt = (idx: number, imagePrompt: FormImagePrompt | null) => {
    // 局部更新
    const draft = [...store.getState().sceneFormParams.imagePrompts];
    draft[idx] = imagePrompt;
    updateTemplateFormParams('imagePrompts', draft);
  };

  const updateSettings = (settings: TemplateSettings) => {
    updateTemplateFormParams('settings', settings);
  };

  useEffect(() => {
    const handleGetTemplateDetail = ({ data, error }: { data?: TemplateData; error?: ServiceError }) => {
      if (error) {
        toast({
          type: 'warning',
          message: error.message,
        });
      }
      if (data) {
        const isMergeImage =
          data.default_settings?.image_upload_setting?.merge_image || data.visible_styles?.web === 'step_merge_image'; // step_merge_image 单步拼图：最多上传两张图片，两张图片需拼图
        const maxImageCount =
          data.default_settings?.image_upload_setting?.max_upload_image_count || // 优先使用 image_upload_setting 中的值
          (data.visible_styles?.web === 'step_multi_image' // 如果没有，则使用 visible_styles 判断， step_multi_image（单步多图）场景，默认支持2张图
            ? 2
            : 1);

        const formData: Partial<TemplateFormParams> = {
          isMergeImage,
          maxImageCount,
          templateId: data.id,
          templateScene: data.scene,
          creationResolution: data.creation_resolution ?? DEFAULT_CREATION_RESOLUTION,
        };
        if (data.estimated_duration) {
          formData.estimatedDuration = data.estimated_duration;
        }
        setFormParams('scene', formData);

        setTemplateDescription(data.instructions?.long_text ?? '');
        // 图例
        if (data.examples && data.examples.length > 0) {
          const example = data.examples.find((example) => example.name === 'web');
          setExampleImage(example?.resource ?? null);
        }

        // 初始化图片输入框
        setImageUploadCount(maxImageCount);

        if (data.preset_settings) {
          setImageUploadHints(data.preset_settings.upload_hints ?? []);

          const materials = new Array(maxImageCount).fill(null);
          data.preset_settings.materials?.forEach((sample) => {
            materials[sample.index ?? 0] = sample.materials?.map((idea) => ({
              // id 废弃了
              imageUrl: idea.uri ?? '',
            }));
          });
          // 如果有非 null 项，则设置 imageSamples
          if (materials.some((material) => material !== null)) {
            setImageSamples(materials);
          } else {
            setImageSamples([]);
          }
        } else {
          setImageSamples([]);
        }

        // 设置支持的设置类型
        const supportedSettings = getSupportedSettingsFromTemplate(data);
        setSupportedSettings(supportedSettings);

        // 清理/补全 settings：依据新模板的能力移除不支持字段，并修正不在可选项内的取值
        const sanitized = sanitizeTemplateSettings(settings, supportedSettings, data.default_settings);
        if (sanitized && Object.keys(sanitized).length > 0) {
          updateSettings(sanitized);
        } else if (settings && Object.keys(settings).length > 0) {
          updateSettings({});
        }

        if (
          data.crop_type === 'force' &&
          data.default_settings?.crop_ratio &&
          CROP_RATIO_PATTERN.test(data.default_settings.crop_ratio)
        ) {
          //强制裁剪：crop_type=force，此时取 default_settings?.crop_ratio 作为唯一比例
          setForceCropRatio(data.default_settings.crop_ratio);
        } else if (data.crop_type === 'active' && data.supported_settings?.crop_ratios?.length) {
          //自由裁剪：crop_type=active，此时取 supported_settings?.crop_ratios 作为可选比例，取 default_settings?.crop_ratio 作为默认比例
          const filteredCropRatios = data.supported_settings.crop_ratios.filter(
            (ratio) => CROP_RATIO_PATTERN.test(ratio) || ratio === 'free',
          );
          setActiveCropRatios(filteredCropRatios);
          if (data.default_settings?.crop_ratio) {
            const defaultCropIndex = filteredCropRatios.indexOf(data.default_settings?.crop_ratio);
            setDefaultCropIndex(defaultCropIndex === -1 ? 0 : defaultCropIndex);
          }
        }
      }
    };

    handleGetTemplateDetail(templateDetail);
  }, [templateDetail]);

  // 检查用户套餐是否支持模版设置
  useEffect(() => {
    if (!settings) return;

    const { settings: validSettings, hasInvalidSetting } = validateUserPermissionTemplateSettings({
      settings,
      userSubsPlan: userInfo?.subs_plan,
    });

    if (hasInvalidSetting) {
      toast({
        type: 'info',
        message: t('popup.toast.invalidSetting'),
      });
      updateSettings(validSettings);
    }
  }, [settings, userInfo]);

  const validationErrorMessage = useMemo(() => {
    // 检查图片
    const hasValidImage = imagePrompts.slice(0, imageUploadCount).filter((p) => p?.prompt?.sourceImage).length > 0;
    if (!hasValidImage) {
      return t('create.createForm.error.imageEmpty');
    }

    return '';
  }, [imagePrompts, imageUploadCount]);

  const handleChangeFiles = async (
    idx: number,
    uploadFile: ImageUploadFile,
    uploadFileInfo?: ImageUploadFileInfo,
  ): Promise<FormImagePrompt | null | undefined> => {
    if (uploadFile.status === 'removed') {
      updateImagePrompt(idx, null);

      // 如果另一张图有 mergeImage，移除 mergeImage
      const otherIdx = idx === 0 ? 1 : 0;
      const otherImagePrompt = imagePrompts[otherIdx];
      if (otherImagePrompt?.prompt?.mergeImage) {
        otherImagePrompt.prompt.mergeImage = undefined;
        updateImagePrompt(otherIdx, otherImagePrompt);
      }
      return;
    }
    if (uploadFile.status === 'uploading') {
      setUploadingImages((prev) => [...prev, idx]);

      return;
    }

    // 上传完成或上传失败时，减少上传计数
    if (uploadFile.status === 'done' || uploadFile.status === 'error') {
      setUploadingImages((prev) => prev.filter((i) => i !== idx));
    }

    // 不管上传成功与否，都要上报事件
    if (uploadFileInfo) {
      reportEvent('upload_image', {
        interaction_position: 'template',
        ...uploadFileInfo,
      });
    }

    // 如果上传失败，不更新图片
    if (uploadFile.status === 'error') {
      return;
    }

    const files = [...store.getState().sceneFormParams.imagePrompts];
    const newImagePrompt = {
      id: uploadFile.id,
      status: 'done',
      prompt: uploadFile.file,
    } as FormImagePrompt;
    files[idx] = newImagePrompt;

    // 判断是否需要合并图片
    if (files.length === 2 && files[0] && files[1] && formParams.isMergeImage) {
      setProcessingImageIdx(idx);
      triggerMergeOnUpload(files[0], files[1]).finally(() => {
        setProcessingImageIdx(null);
      });
    }
    return newImagePrompt;
  };

  const { setCollapsed } = useTemplatesPanel();

  return (
    <div className="-translate-x-1/2 pointer-events-auto absolute bottom-4 left-1/2 flex h-[68px] items-center gap-x-2 rounded-16 border border-system-white08 bg-[#1A1C20]/[.72] p-3 backdrop-blur-3xl sm:bottom-0 sm:h-fit sm:w-full sm:flex-col sm:rounded-b-none">
      {/* 模版封面 */}
      <div className="group relative flex h-[96px] w-[72px] shrink-0 cursor-pointer items-end self-end rounded-12 sm:hidden">
        <TemplateCard
          variant="portrait"
          resource={resource}
          title={''}
          titleClassName="text-xs"
          className="border border-system-white16"
        />
        <div
          className="absolute inset-0 z-10 hidden flex-col items-center justify-center gap-2 rounded-12 bg-system-black36 group-hover:flex"
          onClick={() => {
            // 展开模板选择面板
            setCollapsed(false);
          }}
        >
          <ProductLineElementLibrary className="text-2xl" />
          <span className="text-xs">{t('templates.changeTemplate')}</span>
        </div>
      </div>

      <div className="mx-2 h-11 w-px shrink-0 bg-system-white08 sm:hidden" />

      <div className="flex h-12 w-full items-center gap-2 sm:items-end sm:gap-4 sm:px-3">
        <CustomTooltip
          content={
            // 如果imageSamples有值，则不显示模板描述
            !imageSamples?.length && (exampleImage || templateDescription) && supportsHover ? (
              <TemplateDescription exampleImage={exampleImage} templateDescription={templateDescription} />
            ) : undefined
          }
          trigger="hover"
          contentClassName="flex gap-4 items-center"
        >
          <div className="relative flex h-[96px] items-center justify-center gap-4 self-end">
            <ImageUploadSection
              imageUploadCount={imageUploadCount}
              imageUploadHints={imageUploadHints}
              imagePrompts={imagePrompts}
              maxImageSize={maxImageSize}
              processingImageIdx={processingImageIdx}
              uploadingImages={uploadingImages}
              handleChangeFiles={handleChangeFiles}
              updateImagePrompt={updateImagePrompt}
              forceCropRatio={forceCropRatio}
              activeCropRatios={activeCropRatios}
              defaultCropIndex={defaultCropIndex}
              imageSamples={imageSamples}
              templateId={templateDetail.data?.id}
              allowTempMode={true}
              ref={flyEndTargetRef}
            />
          </div>
        </CustomTooltip>

        <div className="mx-2 h-11 w-px shrink-0 bg-system-white08 sm:hidden" />

        <SettingsSection settings={settings} supportedSettings={supportedSettings} updateSettings={updateSettings} />
      </div>

      <div className="h-9 min-w-[124px] shrink-0 sm:mt-7 sm:h-12 sm:w-full">
        <FormSubmitButton
          className="h-full rounded-10 px-4 text-sm sm:text-base"
          disabled={uploadingImages.length > 0 || processingImageIdx !== null}
          disabledTip={validationErrorMessage}
          taskType="scene"
        >
          {t('create.createForm.submit')}
        </FormSubmitButton>
      </div>
    </div>
  );
};

export default TemplateForm;
